/**
 * AdminExportsPage — manage CSV export tokens for Google Sheets integration.
 *
 * Use case: Founder pastes the IMPORTDATA formulas into Google Sheets so the
 * daily-log + zone-summary auto-refresh hourly without manual data entry.
 * One sheet becomes a live customer demo of TurfPulse skip decisions.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { ArrowLeft, Plus, Copy, Trash, FileText } from '@phosphor-icons/react';
import { API_URL } from '../config/api';
import { SEO } from '../components/SEO';

const withAuth = () => ({ withCredentials: true });

const copy = (text, label) => {
  if (navigator.clipboard) {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied`);
  }
};

const AdminExportsPage = () => {
  const navigate = useNavigate();
  const [tokens, setTokens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState('Default');
  const [minting, setMinting] = useState(false);
  const [justMinted, setJustMinted] = useState(null);

  const load = async () => {
    try {
      const r = await axios.get(`${API_URL}/api/exports/tokens`, withAuth());
      setTokens(r.data.items || []);
    } catch (e) {
      if (e.response?.status === 403) { toast.error('Admin only'); navigate('/'); return; }
      toast.error('Could not load tokens');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const mint = async () => {
    if (!newName.trim()) return;
    setMinting(true);
    try {
      const r = await axios.post(`${API_URL}/api/exports/tokens`, { name: newName.trim() }, withAuth());
      setJustMinted(r.data);
      setNewName('Default');
      toast.success('Token minted — copy it now, you cannot retrieve it again');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Mint failed');
    } finally {
      setMinting(false);
    }
  };

  const revoke = async (id) => {
    if (!window.confirm('Revoke this token? Any Google Sheet using it will stop working.')) return;
    try {
      await axios.delete(`${API_URL}/api/exports/tokens/${id}`, withAuth());
      toast.success('Revoked');
      load();
    } catch (e) {
      toast.error('Revoke failed');
    }
  };

  return (
    <div className="min-h-screen bg-background" data-testid="admin-exports-page">
      <SEO title="Google Sheets Exports — TurfPulse" description="" />
      <header className="border-b border-border/60 bg-card/80 backdrop-blur-sm sticky top-0 z-30">
        <div className="max-w-4xl mx-auto flex items-center justify-between px-4 py-3">
          <button onClick={() => navigate(-1)} className="flex items-center gap-1.5 text-sm text-muted-foreground" data-testid="exports-back">
            <ArrowLeft size={14} /> Back
          </button>
          <span className="text-sm font-bold flex items-center gap-1.5"><FileText size={14} weight="fill" /> Google Sheets Exports</span>
          <div className="w-12" />
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        {/* How it works */}
        <Card>
          <CardContent className="p-5 space-y-3 text-sm">
            <h2 className="font-bold text-base">How it works</h2>
            <ol className="list-decimal pl-4 space-y-1.5 text-muted-foreground">
              <li>Mint a token below (give it a friendly name like &quot;Customer Demo&quot;).</li>
              <li>Copy the two <code className="bg-muted px-1 rounded">=IMPORTDATA(...)</code> formulas shown after minting.</li>
              <li>In your Google Sheet, paste one formula in cell A1 of a sheet called <code className="bg-muted px-1 rounded">Daily Log</code>, the other in A1 of <code className="bg-muted px-1 rounded">Zone Summary</code>.</li>
              <li>Done. Google refreshes every hour automatically. Manual refresh: <em>File → Refresh imports</em>.</li>
            </ol>
            <p className="text-xs text-muted-foreground italic pt-2 border-t">
              Token grants read-only CSV access to <strong>your data</strong>. Anyone with the URL can read it — treat the URL like a password. Revoke instantly here if a sheet is shared too widely.
            </p>
          </CardContent>
        </Card>

        {/* Mint new */}
        <Card>
          <CardContent className="p-5">
            <h2 className="font-bold text-sm mb-2">Mint a new token</h2>
            <div className="flex items-center gap-2">
              <Input
                value={newName}
                onChange={e => setNewName(e.target.value)}
                placeholder="Name (e.g. Customer Demo, Internal, Annual Report)"
                className="text-sm"
                data-testid="mint-name-input"
              />
              <Button onClick={mint} disabled={minting} data-testid="mint-btn"><Plus size={14} className="mr-1" />Mint</Button>
            </div>
          </CardContent>
        </Card>

        {/* Just-minted token (one-time view) */}
        {justMinted && (
          <Card className="border-emerald-500 border-2" data-testid="just-minted-card">
            <CardContent className="p-5 space-y-3">
              <div className="flex items-center justify-between">
                <h2 className="font-bold text-sm text-emerald-700 dark:text-emerald-400">✅ {justMinted.name} — copy now, this is the only time you&apos;ll see it</h2>
                <Button variant="ghost" size="sm" onClick={() => setJustMinted(null)}>Dismiss</Button>
              </div>

              <div className="space-y-1.5">
                <label className="text-[10px] uppercase tracking-wider text-muted-foreground">Raw token</label>
                <div className="flex items-center gap-2">
                  <code className="flex-1 text-xs bg-muted p-2 rounded font-mono break-all">{justMinted.token}</code>
                  <Button size="sm" variant="outline" onClick={() => copy(justMinted.token, 'Token')}><Copy size={12} /></Button>
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="text-[10px] uppercase tracking-wider text-muted-foreground">📋 Daily Log — paste into A1 of &quot;Daily Log&quot; sheet</label>
                <div className="flex items-center gap-2">
                  <code className="flex-1 text-xs bg-muted p-2 rounded font-mono break-all" data-testid="sheets-daily-formula">{justMinted.sheets_daily}</code>
                  <Button size="sm" onClick={() => copy(justMinted.sheets_daily, 'Daily-log formula')}><Copy size={12} className="mr-1" />Copy</Button>
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="text-[10px] uppercase tracking-wider text-muted-foreground">📊 Zone Summary — paste into A1 of &quot;Zone Summary&quot; sheet</label>
                <div className="flex items-center gap-2">
                  <code className="flex-1 text-xs bg-muted p-2 rounded font-mono break-all" data-testid="sheets-summary-formula">{justMinted.sheets_summary}</code>
                  <Button size="sm" onClick={() => copy(justMinted.sheets_summary, 'Zone-summary formula')}><Copy size={12} className="mr-1" />Copy</Button>
                </div>
              </div>

              <details className="text-xs text-muted-foreground">
                <summary className="cursor-pointer">Raw CSV URLs (advanced)</summary>
                <div className="mt-2 space-y-1 pl-3">
                  <div><strong>Daily log:</strong> <code className="break-all">{justMinted.daily_log_url}</code></div>
                  <div><strong>Zone summary:</strong> <code className="break-all">{justMinted.zone_summary_url}</code></div>
                </div>
              </details>
            </CardContent>
          </Card>
        )}

        {/* Existing tokens */}
        <div>
          <h2 className="text-sm font-bold mb-2">Active tokens <span className="text-[10px] font-normal text-muted-foreground">({tokens.filter(t => !t.revoked).length})</span></h2>
          {!loading && !justMinted && tokens.filter(t => !t.revoked).length > 0 && (
            <Card className="mb-2 border-amber-400 border bg-amber-50/40 dark:bg-amber-950/20" data-testid="lost-formula-hint">
              <CardContent className="p-3 text-xs text-amber-900 dark:text-amber-200">
                💡 <strong>Lost the IMPORTDATA formulas?</strong> They&apos;re shown <strong>once</strong> right after minting (security — only the hash is stored). Just mint a fresh token above — your old ones keep working until you revoke them.
              </CardContent>
            </Card>
          )}
          {loading && <p className="text-xs text-muted-foreground">Loading…</p>}
          {!loading && tokens.length === 0 && (
            <Card><CardContent className="p-4 text-xs text-muted-foreground text-center">No tokens yet. Mint your first one above.</CardContent></Card>
          )}
          <div className="space-y-2">
            {tokens.map(t => (
              <Card key={t.id} data-testid={`token-row-${t.id}`} className={t.revoked ? 'opacity-50' : ''}>
                <CardContent className="p-3 flex items-center justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-sm">{t.name}</span>
                      <code className="text-[10px] text-muted-foreground">{t.token_prefix}…</code>
                      {t.revoked && <span className="text-[10px] px-1.5 py-0.5 bg-rose-500/20 text-rose-700 dark:text-rose-400 rounded uppercase font-semibold">Revoked</span>}
                    </div>
                    <div className="text-[10px] text-muted-foreground mt-0.5">
                      Created {new Date(t.created_at).toLocaleDateString()}
                      {t.last_used_at && ` · Last used ${new Date(t.last_used_at).toLocaleString()}`}
                    </div>
                  </div>
                  {!t.revoked && (
                    <Button variant="ghost" size="sm" onClick={() => revoke(t.id)} className="text-rose-500" data-testid={`revoke-${t.id}`}>
                      <Trash size={14} />
                    </Button>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </main>
    </div>
  );
};

export default AdminExportsPage;
